import React from 'react';
import type { ImageModifier } from '../Image';
import { fallbackWidth } from '../Image';
import { Image as ImageType, Orientation } from '../../../../types/api/ImageType';
import { getQuerySeparator } from '../Responsive';
import bem from '../../../../lib/bemUtils/bem';
import { useGlobalPrintMode } from '../../../../contexts/GlobalPrintMode';

type Img = {
  image: ImageType;
  maxSize?: number;
  modifier?: ImageModifier | ImageModifier[];
  lazyLoading?: boolean;
  getWidthWithQueryParameter: (maxSize?: number) => number | undefined;
  getAlt: () => string;
};

/**
 * Returns an <img /> depending on src and availableWidths.
 * All fallback images have a fixed width of 1024.
 * Images that are not responsive don't have a fixed width
 * like default images or similar.
 */
export function Img({ image, maxSize, modifier, lazyLoading, getWidthWithQueryParameter, getAlt }: Img) {
  const { isInGlobalPrintMode } = useGlobalPrintMode();

  const getHeight = (orientation: Orientation): number => {
    return orientation === 'landscape' ? 576 : 1152;
  };

  const getImageWidth = () => {
    return getWidthWithQueryParameter(maxSize || fallbackWidth);
  };

  // check if image.src already has width query parameter from backend.
  // if so, for example person info card, do not add another query parameter width!!!
  const getSrc = () => {
    const imageWidth = getImageWidth();

    if (image.src.includes('width=') || !imageWidth) {
      return image.src;
    }

    return `${image.src}${getQuerySeparator(image.src)}width=${imageWidth}`;
  };

  return (
    <img
      className={bem('image', modifier)}
      src={getSrc()}
      alt={getAlt()}
      width={getImageWidth()}
      /* height is set to support lazy loading algorithm to provide better estimation.
       *  Currently fixed numbers as educated guess. Should be replaced by height of image provided by backend. */
      height={lazyLoading ? getHeight(image.orientation) : undefined}
      loading={!isInGlobalPrintMode && lazyLoading ? 'lazy' : undefined}
    />
  );
}
